import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import type { DomainCageOverviewSceneLabel } from '@/domain/cages/overview.types';
import {
    getConfiguredSceneLabelColor,
    MIN_RELATIVE_COUNT,
    resolveSceneLabelColor,
    resolveSceneLabelName,
} from '@/utils/sceneLabelHelpers';

import BioscopeSceneLabelsChart from './BioscopeSceneLabelsChart';

// The scene labels showed on UI is controlled by table `scene_label`.
// Add/remove coresponding rows to table to use labels from bioscope.
// There is no need to have extra filter or sorts on UI.
// UI shows the lables it receives from backend and labels must be
// generated only for labels described in `scene_label` table.
// Good part is - it is a backend or service job.

interface SceneLabelProps {
    /** The overview's grouped scene-label counts. */
    sceneLabels: DomainCageOverviewSceneLabel[];
    isLoadingOverview: boolean;
}

export default function SceneLabels({ sceneLabels, isLoadingOverview }: SceneLabelProps) {
    const { i18n } = useTranslation(['bioscopeStatus']);
    const [data, setData] = useState<number[]>([]);
    const [relativeCounts, setRelativeCounts] = useState<number[]>([]);
    const [labels, setLabels] = useState<string[]>([]);
    const [colors, setColors] = useState<string[]>([]);

    const constructSceneLabelDataOnDataChange = useCallback(() => {
        if (!isLoadingOverview) {
            const sortedLabels = sceneLabels
                .filter((label) => label.relativeCount >= MIN_RELATIVE_COUNT)
                .sort((a, b) => b.count - a.count);

            let fallbackColorIndex = 0;
            const newColors = sortedLabels.map((label) => {
                const color = resolveSceneLabelColor(label.sceneLabelTag, fallbackColorIndex);
                if (!getConfiguredSceneLabelColor(label.sceneLabelTag)) {
                    fallbackColorIndex++;
                }
                return color;
            });
            const newData = sortedLabels.map((label) => label.count);
            const newRelativeCounts = sortedLabels.map((label) => label.relativeCount);
            const newLabels = sortedLabels.map((label) =>
                resolveSceneLabelName(label.sceneLabelTag)
            );

            setData(newData);
            setRelativeCounts(newRelativeCounts);
            setLabels(newLabels);
            setColors(newColors);
        }
    }, [sceneLabels, isLoadingOverview, i18n.language]);

    useEffect(() => {
        constructSceneLabelDataOnDataChange();
    }, [constructSceneLabelDataOnDataChange]);

    return (
        <div data-tut="sceneLabels">
            {isLoadingOverview ? (
                <Spinner size="lg" />
            ) : (
                <BioscopeSceneLabelsChart
                    data={data}
                    colors={colors}
                    labels={labels}
                    relativeCounts={relativeCounts}
                />
            )}
        </div>
    );
}
